// F07 — Offline-status + reisemodus
// Holder rede på hvor mange dager som er forhåndslastet, og lar brukeren
// trigge en «pakk for offline»-fyll før kritiske etapper (cruise, biltur).

import { DAYS } from './data.js';

const STORAGE_KEY = 'ca26offline_v1';

let state = {
  ready: false,                    // SW er klar og minst app-shell er cachet
  daysReady: 0,                    // antall dager der vi har tile-coverage
  lastPrecache: null,              // timestamp for siste vellykkede precache
  storageUsed: 0,                  // bytes brukt i origin storage
  storageQuota: 0,                 // total tilgjengelig (estimert)
  trip: { total: 0, done: 0, failed: 0, running: false, label: null },
};
const listeners = new Set();

function load(){
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    state = { ...state, ...parsed, trip: { ...state.trip, running: false } };
  } catch {}
}

function persist(){
  try {
    const { daysReady, lastPrecache } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ daysReady, lastPrecache }));
  } catch {}
}

function notify(){ listeners.forEach(fn => { try { fn(state); } catch {} }); }

export function onOfflineChange(fn){
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getOfflineState(){ return state; }

async function measureStorage(){
  if (!('storage' in navigator) || !navigator.storage.estimate) return;
  try {
    const est = await navigator.storage.estimate();
    state.storageUsed = est.usage || 0;
    state.storageQuota = est.quota || 0;
  } catch {}
}

// Reisemål med koordinater — må matche URL-formatet appen faktisk bruker
// (/api/weather?lat=..&lng=..), ellers treffer ikke offline-cachen.
const PRECACHE_WX_LOCS = [
  {lat:49.28,lng:-123.12}, // Vancouver
  {lat:57.05,lng:-135.33}, // Inside Passage
  {lat:58.30,lng:-134.42}, // Juneau
  {lat:50.67,lng:-120.33}, // Kamloops
  {lat:52.87,lng:-118.08}, // Jasper
  {lat:51.18,lng:-115.57}, // Banff
  {lat:51.05,lng:-114.07}, // Calgary
];

// Bygger et sett URL-er som dekker en sammenhengende etappe — brukes når
// brukeren trykker «reisemodus». Kart-tiles genereres for bbox rundt rute.
function buildPrecacheUrls(fromIdx, toIdx){
  const urls = new Set();
  // Værprognose for alle reisemål — samme URL-er som loadWeather() bruker,
  // så de serveres fra cache når brukeren er offline.
  for (const c of PRECACHE_WX_LOCS) {
    urls.add(`/api/weather?lat=${c.lat}&lng=${c.lng}`);
  }
  return [...urls];
}

export async function precacheTrip(fromIdx, toIdx, label){
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg || !reg.active) return false;
  const urls = buildPrecacheUrls(fromIdx, toIdx);
  state.trip = { total: urls.length, done: 0, failed: 0, running: true, label };
  notify();
  reg.active.postMessage({ type: 'PRECACHE_TRIP', urls, label });
  return true;
}

export async function refreshStatus(){
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg || !reg.active) return;
  reg.active.postMessage({ type: 'OFFLINE_STATUS' });
  await measureStorage();
  notify();
}

export function initOffline(){
  load();

  if (!('serviceWorker' in navigator)) return;

  // Auto-oppdatering: når en ny service worker tar over (etter ny deploy),
  // last siden på nytt ÉN gang så brukeren aldri blir sittende fast på gammel
  // kode. Uten dette må man manuelt tømme cache — årsaken til at appen
  // «ikke oppdaterte seg» og at ny innlogging/UI ikke dukket opp.
  if (navigator.serviceWorker.controller) {
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  }

  navigator.serviceWorker.register('/sw.js').then((reg) => {
    state.ready = true;
    notify();
    refreshStatus();
    // Se etter ny versjon straks + hver time mens appen er åpen.
    try { reg.update(); } catch {}
    setInterval(() => { try { reg.update(); } catch {} }, 60 * 60 * 1000);
  }).catch(() => {});

  navigator.serviceWorker.addEventListener('message', (e) => {
    const m = e.data || {};
    if (m.type === 'TRIP_PRECACHED') {
      state.trip = { total: m.total, done: m.done, failed: m.failed, running: false, label: m.label };
      state.lastPrecache = Date.now();
      if (m.done > 0) {
        // Grov heuristikk: hver vellykket gruppe er en dag
        const newDays = Math.min(DAYS.length, state.daysReady + Math.ceil(m.done / 1));
        state.daysReady = newDays;
      }
      persist();
      measureStorage().then(notify);
    }
    if (m.type === 'OFFLINE_STATUS_REPORT') {
      // App-shell teller som «1 dag klar» (selve appen + cachelogikk)
      state.ready = m.appCount > 0;
      notify();
    }
  });

  window.addEventListener('online', notify);
  window.addEventListener('offline', notify);

  measureStorage().then(notify);
}

// Returnerer hvor mange MB som er brukt — for visning i status-bar.
export function formatStorage(state){
  const used = state.storageUsed || 0;
  if (used < 1024 * 1024) return `${Math.round(used / 1024)} kB`;
  return `${(used / 1024 / 1024).toFixed(1)} MB`;
}

// Hva er neste «kritiske» etappe der vi trenger offline-data?
// Brukes til å vise «cruise 28. juli» eller «biltur 26. juli» i statusbåndet.
export function nextOfflineMilestone(){
  const now = new Date();
  const milestones = [
    { date: '2026-07-19', label: 'cruise 19. juli',   icon: '⚓︎' },
    { date: '2026-07-26', label: 'biltur 26. juli',   icon: '→'  },
    { date: '2026-07-28', label: 'Banff 28. juli',    icon: '△'  },
  ];
  for (const m of milestones) {
    if (new Date(m.date + 'T00:00:00+02:00') > now) return m;
  }
  return null;
}
